/*!
 * Scroll Spy
 *
 * @author Grigory Zarubin (https://craigy.ru/)
 */

class ScrollSpy {

  constructor(headings, linksContainer, options = {}) {
    this._headings = headings;
    this._linksContainer = linksContainer;
    this._offsetTop = options?.offsetTop || 0;
    this._onActive = options?.onActive || null;

    this._headingsOffsets = [];
    this._links = [];
    this._activeLink = null;
    this._observer = null;

    this.init();
  }


  init() {
    // We use IntersectionObserver interface cause it much more faster than window.onScroll in our case
    this._observer = new IntersectionObserver(entries => this._observerCallback(entries), {
      root: null,
      rootMargin: this._offsetTop + 'px 0px 0px 0px',
      threshold: [0, 1]
    });
    this._headings.forEach((heading) => {
      if (this._cacheHeadingOffsetTop(heading)) {
        this._observer.observe(heading);
      }
    });

    // Update the headings' offsetTop values
    window.addEventListener('resize', () => {
      clearTimeout(window.resizeFinished);

      window.resizeFinished = setTimeout(() => {
        this._headingsOffsets = [];
        this._headings.forEach((heading) => {
          this._cacheHeadingOffsetTop(heading);
        });

        // Update the active link as well
        this._observerCallback();
      }, 300);
    }, false);

    // Cache the links and make them smoothly scrollable
    this._linksContainer.querySelectorAll('a').forEach((link, index) => {
      // Since links on the page are created from headings, the order will be the same for both arrays of links and headings
      this._links.push(link);

      link.addEventListener('click', (event) => {
        event.preventDefault();

        this.smoothScroll(index);
      }, false);
    });
  }

  smoothScroll(index) {
    const onActiveCallback = this._onActive;
    const scrollHandler = () => {
      clearTimeout(window.scrollEnded);

      window.scrollEnded = setTimeout(() => {
        this._onActive = onActiveCallback;
        this._onActiveCallback(index);

        window.removeEventListener('scroll', scrollHandler);
      }, 300);
    };

    this._onActive = null;

    window.addEventListener('scroll', scrollHandler, { passive: true });

    window.scrollTo({
      top: index < 1 ? 0 : this._headingsOffsets[index] + 1,
      behavior: 'smooth'
    });
  }


  _observerCallback(entries) {
    // The result of entries' check is unpredictable (see more: https://stackoverflow.com/questions/61951380/intersection-observer-fails-sometimes-when-i-scroll-fast), so we just check offsetTop
    const currentScrollTop = document.documentElement.scrollTop;
    const updateHash = (hash) => { // affects History
      const url = new URL(window.location.href);

      url.hash = hash;
      window?.history?.replaceState(null, null, url.href);
    };

    let index = this._findNearestIndexLessOrEqualThan(this._headingsOffsets, currentScrollTop);

    if (index < 1) {
      index = 0;
    }

    const activeLink = this._links[index];

    if (!this._activeLink || (this._activeLink && this._activeLink !== activeLink)) {
      if (this._activeLink) {
        this._activeLink.classList.remove('active');
      }
      this._activeLink = activeLink;
      this._activeLink.classList.add('active');

      updateHash(this._headings[index].getAttribute('id'));

      this._onActiveCallback(index);
    }
  }

  _cacheHeadingOffsetTop(heading) {
    const id = heading.getAttribute('id');

    if (id) {
      this._headingsOffsets.push(document.getElementById(id).offsetTop + this._offsetTop);
    }

    return id;
  }

  _onActiveCallback(index) {
    if (typeof this._onActive == 'function') {
      this._onActive(index, this._activeLink);
    }
  }

  _findNearestIndexLessOrEqualThan(array, target) {
    // Let's use the binary search for better performance
    let start = 0,
        end = array.length - 1,
        middle,
        item,
        number = -1;

    if (array[end] < target) {
      return end;
    }

    while (start <= end) {
      middle = (start + end) >> 1;
      item = array[middle];

      if (item > target) {
        end = middle - 1;
      } else if (item <= target) {
        number = middle;
        start = middle + 1;
      } else {
        return start;
      }
    }

    return number;
  }

}